// VendorProductUpdate.jsx
import React, { useState, useEffect } from "react";
import {
  FiPlus,
  FiTrash2,
  FiUpload,
  FiX,
  FiImage,
  FiChevronDown,
  FiSave,
  FiRotateCw
} from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import PageView from "../../../../../../components/commonComponents/Viewpage";
import {
  useGetCategoriesQuery,
  useGetProductByIdQuery,
  useUpdateProductMutation
} from "../../../../../../store/api/VendorcatalogApi";
import { useGetVendorWarehousesQuery } from "../../../../../../store/api/VendorWarehouseApi";

/**
 * International Standard Product Update Component
 * Supports selective updates for images, stock, and other fields
 */
function VendorProductUpdate() {
  const { id } = useParams();
  const navigate = useNavigate();

  // API Hooks
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useGetCategoriesQuery();

  const {
    data: productData,
    isLoading: productLoading,
    error: productError,
    refetch: refetchProduct
  } = useGetProductByIdQuery(id);

  const {
    data: warehousesData,
    isLoading: warehousesLoading
  } = useGetVendorWarehousesQuery();

  const [updateProduct, {
    isLoading: updatingProduct,
    isSuccess: updateSuccess
  }] = useUpdateProductMutation();

  // State Management
  const [product, setProduct] = useState({
    id: '',
    title: '',
    description: '',
    categories: [],
    primary_category: '',
    main_image: null,
    main_image_url: null,
    variants: []
  });

  const [errors, setErrors] = useState({});
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);

  // Constants
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  // Computed Values
  const primaryCategories = categoriesData?.data?.filter(cat => cat.parent === null) || [];
  const warehouses = warehousesData || [];

  // Effects
  useEffect(() => {
    if (productData) {
      initializeFormData(productData);
    }
  }, [productData]);

  useEffect(() => {
    if (updateSuccess) {
      handleUpdateSuccess();
    }
  }, [updateSuccess]);

  // Initialization
  const initializeFormData = (productData) => {
    const product = productData;

    setProduct({
      id: product.id || '',
      title: product.title || '',
      description: product.description || '',
      categories: product.categories?.map(cat => cat.toString()) || [],
      primary_category: product.primary_category?.toString() || '',
      main_image: null,
      main_image_url: product.main_image,
      variants: product.variants?.map(variant => ({
        id: variant.id,
        attributes: variant.attributes || '',
        title: variant.title || '',
        gst: variant.gst || '',
        mrp: variant.mrp || '',
        price: variant.price || '',
        discount: variant.discount || '',
        stock: variant.stock || '',
        weight: variant.weight || '',
        length: variant.length || '',
        width: variant.width || '',
        height: variant.height || '',
        currency: variant.currency || 'INR',
        warehouse_id: variant.warehouses?.[0]?.warehouse?.toString() || '', 
        images: variant.images?.map(img => ({
          id: img.id,
          image: null,
          image_url: img.image,
          alt_text: img.alt_text || '',
          should_delete: false
        })) || [{ image: null, alt_text: '' }]
      })) || [{
        attributes: '',
        title: '',
        gst: '',
        mrp: '',
        price: '',
        discount: '',
        stock: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        currency: 'INR',
        warehouse_id: '',
        images: [{ image: null, alt_text: '' }]
      }]
    });
  };

  // Event Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: value
    }));
    clearError(name);
  };

  const handlePrimaryCategoryChange = (categoryId) => {
    const newPrimaryCategory = categoryId.toString();

    setProduct(prev => ({
      ...prev,
      primary_category: newPrimaryCategory,
      categories: prev.categories.includes(newPrimaryCategory)
        ? prev.categories
        : [...prev.categories, newPrimaryCategory]
    }));

    clearError('primary_category');
  };

  const handleCategoryToggle = (categoryId) => {
    setProduct(prev => {
      const currentCategories = [...prev.categories];
      const categoryIndex = currentCategories.indexOf(categoryId.toString());

      if (categoryIndex > -1) {
        currentCategories.splice(categoryIndex, 1);
      } else {
        currentCategories.push(categoryId.toString());
      }

      return {
        ...prev,
        categories: currentCategories
      };
    });
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validationError = validateImageFile(file);
      if (validationError) {
        setErrors(prev => ({ ...prev, main_image: validationError }));
        return;
      }

      setProduct(prev => ({
        ...prev,
        main_image: file,
        main_image_url: URL.createObjectURL(file)
      }));
      clearError('main_image');
    }
  };

  const handleVariantChange = (variantIndex, field, value) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex ? { ...variant, [field]: value } : variant
      )
    }));
    clearError(`variant_${variantIndex}_${field}`);
  };

  const handleVariantImageChange = (variantIndex, imageIndex, field, value, filename = '') => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
            ...variant,
            images: variant.images.map((img, imgIndex) => {
              if (imgIndex === imageIndex) {
                const updatedImage = { ...img, [field]: value };

                // Auto-generate alt text when image is selected
                if (field === 'image' && value && (!img.alt_text || img.alt_text === '')) {
                  updatedImage.alt_text = generateAltTextFromFilename(filename || value.name);
                }

                return updatedImage;
              }
              return img;
            })
          }
          : variant
      )
    }));
  };

  // Image Management
  const validateImageFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, WebP)';
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return 'Image size should be less than 5MB';
    }
    return null;
  };

  const generateAltTextFromFilename = (filename) => {
    if (!filename) return '';

    let altText = filename.replace(/\.[^/.]+$/, "");
    altText = altText.replace(/[_-]/g, " ");
    altText = altText.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
    altText = altText.replace(/\s+/g, " ").trim();

    return altText;
  };

  // Variant Management
  const addVariant = () => {
    setProduct(prev => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          attributes: '',
          title: '',
          gst: '',
          mrp: '',
          price: '',
          discount: '',
          stock: '',
          weight: '',
          length: '',
          width: '',
          height: '',
          currency: 'INR',
          warehouse_id: '',
          images: [{ image: null, alt_text: '' }]
        }
      ]
    }));
  };

  const removeVariant = (variantIndex) => {
    if (product.variants.length > 1) {
      setProduct(prev => ({
        ...prev,
        variants: prev.variants.map((variant, index) =>
          index === variantIndex
            ? { ...variant, should_delete: true }
            : variant
        )
      }));
    }
  };

  const addVariantImage = (variantIndex) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
            ...variant,
            images: [...variant.images, { image: null, alt_text: '' }]
          }
          : variant
      )
    }));
  };

  const removeVariantImage = (variantIndex, imageIndex) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
            ...variant,
            images: variant.images.map((img, imgIndex) =>
              imgIndex === imageIndex
                ? { ...img, should_delete: true }
                : img
            )
          }
          : variant
      )
    }));
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!product.title.trim()) newErrors.title = 'Product title is required';
    if (!product.primary_category) newErrors.primary_category = 'Primary category is required';
    if (product.categories.length === 0) newErrors.categories = 'At least one category is required';

    // Validate variants
    product.variants.forEach((variant, index) => {
      if (!variant.should_delete) {
        if (!variant.attributes.trim()) newErrors[`variant_${index}_attributes`] = 'Variant attributes is required';
        if (!variant.title.trim()) newErrors[`variant_${index}_title`] = 'Variant title is required';
        if (!variant.price) newErrors[`variant_${index}_price`] = 'Selling price is required';
        if (!variant.stock) newErrors[`variant_${index}_stock`] = 'Stock quantity is required';
        if (!variant.warehouse_id) newErrors[`variant_${index}_warehouse`] = 'Warehouse is required';

        // Validate variant images
        variant.images.forEach((image, imgIndex) => {
          if (!image.should_delete) {
            if (!image.image && !image.image_url) {
              newErrors[`variant_${index}_image_${imgIndex}`] = 'Variant image is required';
            }
            if (!image.alt_text.trim()) {
              newErrors[`variant_${index}_alt_${imgIndex}`] = 'Alt text is required';
            }
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setOperationInProgress(true);

    try {
      const formData = prepareFormData();
      const result = await updateProduct(formData).unwrap();

      if (result.success) {
        await handleSuccessfulUpdate();
      } else {
        handleUpdateError(result.message || 'Unknown error occurred');
      }
    } catch (error) {
      handleUpdateError(error.message || 'Network error occurred');
    } finally {
      setOperationInProgress(false);
    }
  };

  const prepareFormData = () => {
    const formData = new FormData();

    // Basic product info
    formData.append('id', product.id);
    formData.append('title', product.title);
    formData.append('description', product.description);
    formData.append('primary_category', product.primary_category);

    // Categories - append each category individually as separate entries
    product.categories.forEach(categoryId => {
      formData.append('categories', categoryId);
    });

    // Main image (only if changed)
    if (product.main_image && typeof product.main_image !== 'string') {
      formData.append('main_image', product.main_image);
    }

    // Variants
    product.variants.forEach((variant, i) => {
      if (variant.should_delete) {
        formData.append(`variants[${i}][should_delete]`, 'true');
        if (variant.id) formData.append(`variants[${i}][id]`, variant.id);
      } else {
        // Always include ID for existing variants
        if (variant.id) formData.append(`variants[${i}][id]`, variant.id);

        // Include all variant fields
        formData.append(`variants[${i}][attributes]`, variant.attributes || '');
        formData.append(`variants[${i}][title]`, variant.title || '');
        formData.append(`variants[${i}][gst]`, variant.gst || '');
        formData.append(`variants[${i}][mrp]`, variant.mrp || '');
        formData.append(`variants[${i}][price]`, variant.price || '');
        formData.append(`variants[${i}][discount]`, variant.discount || '');
        formData.append(`variants[${i}][stock]`, variant.stock || '');
        formData.append(`variants[${i}][weight]`, variant.weight || '');
        formData.append(`variants[${i}][length]`, variant.length || '');
        formData.append(`variants[${i}][width]`, variant.width || '');
        formData.append(`variants[${i}][height]`, variant.height || '');
        formData.append(`variants[${i}][currency]`, variant.currency || 'INR');
        formData.append(`variants[${i}][warehouse_id]`, variant.warehouse_id || ''); // Add warehouse_id

        // Variant images
        variant.images.forEach((img, j) => {
          if (img.should_delete) {
            formData.append(`variants[${i}][images][${j}][should_delete]`, 'true');
            if (img.id) formData.append(`variants[${i}][images][${j}][id]`, img.id);
          } else {
            if (img.id) formData.append(`variants[${i}][images][${j}][id]`, img.id);
            formData.append(`variants[${i}][images][${j}][alt_text]`, img.alt_text || '');
            if (img.image && typeof img.image !== 'string') {
              formData.append(`variants[${i}][images][${j}][image]`, img.image);
            }
          }
        });
      }
    });

    return formData;
  };

  const handleSuccessfulUpdate = async () => {
    // Refresh product data
    await refetchProduct();

    // Show success message
    alert('Product updated successfully!');

    // Navigate back
    navigate(-1);
  };

  const handleUpdateError = (errorMessage) => {
    console.error('Product update error:', errorMessage);
    alert(`Error updating product: ${errorMessage}`);
  };

  const handleUpdateSuccess = () => {
    setOperationInProgress(false);
  };

  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  // Helper Functions
  const isCategorySelected = (categoryId) => {
    return product.categories.includes(categoryId.toString());
  };

  const getSelectedCategoryNames = () => {
    return product.categories.map(catId => {
      const category = categoriesData?.data?.find(cat => cat.id.toString() === catId.toString());
      return category?.name || '';
    }).filter(name => name !== '');
  };

  const getChildCategories = () => {
    if (!product.categories.length) return [];

    const childCats = [];
    product.categories.forEach(catId => {
      const children = categoriesData?.data?.filter(childCat => childCat.parent === parseInt(catId)) || [];
      childCats.push(...children);
    });
    return childCats;
  };

  const childCategories = getChildCategories();

  // Loading and Error States
  if (productLoading || categoriesLoading || warehousesLoading) {
    return (
      <PageView title="Edit Product" closeTo="/dashboard">
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <FiRotateCw className="w-5 h-5 animate-spin" />
            <span className="text-lg">Loading product data...</span>
          </div>
        </div>
      </PageView>
    );
  }

  if (productError || categoriesError) {
    return (
      <PageView title="Edit Product" closeTo="/dashboard">
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600 text-lg">
            Error loading product data. Please try again.
          </div>
        </div>
      </PageView>
    );
  }

  // Main Render
  return (
    <PageView title="Edit Product" closeTo="/dashboard">
      <div className="w-full mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Product Information */}
          <ProductBasicInfo
            product={product}
            errors={errors}
            primaryCategories={primaryCategories}
            childCategories={childCategories}
            showCategoryDropdown={showCategoryDropdown}
            onInputChange={handleInputChange}
            onPrimaryCategoryChange={handlePrimaryCategoryChange}
            onCategoryToggle={handleCategoryToggle}
            onMainImageChange={handleMainImageChange}
            onToggleCategoryDropdown={() => setShowCategoryDropdown(!showCategoryDropdown)}
            getSelectedCategoryNames={getSelectedCategoryNames}
            isCategorySelected={isCategorySelected}
          />

          {/* Variants Section */}
          <ProductVariantsSection
            product={product}
            errors={errors}
            warehouses={warehouses}
            onAddVariant={addVariant}
            onRemoveVariant={removeVariant}
            onVariantChange={handleVariantChange}
            onAddVariantImage={addVariantImage}
            onRemoveVariantImage={removeVariantImage}
            onVariantImageChange={handleVariantImageChange}
            validateImageFile={validateImageFile}
          />

          {/* Submit Section */}
          <FormActions
            onCancel={() => navigate(-1)}
            isSubmitting={operationInProgress || updatingProduct}
          />
        </form>
      </div>
    </PageView>
  );
}

// Sub-components for better organization
const ProductBasicInfo = ({
  product, errors, primaryCategories, childCategories, showCategoryDropdown,
  onInputChange, onPrimaryCategoryChange, onCategoryToggle, onMainImageChange,
  onToggleCategoryDropdown, getSelectedCategoryNames, isCategorySelected
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column */}
      <div className="space-y-4">
        <FormField
          label="Product Title *"
          error={errors.title}
        >
          <input
            type="text"
            name="title"
            value={product.title}
            onChange={onInputChange}
            required
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Enter product title"
          />
        </FormField>

        <FormField
          label="Description"
        >
          <textarea
            name="description"
            value={product.description}
            onChange={onInputChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter product description"
          />
        </FormField>
      </div>

      {/* Right Column */}
      <div className="space-y-4">
        <FormField
          label="Primary Category *"
          error={errors.primary_category}
        >
          <select
            name="primary_category"
            value={product.primary_category}
            onChange={(e) => onPrimaryCategoryChange(e.target.value)}
            required
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.primary_category ? 'border-red-500' : 'border-gray-300'
              }`}
          >
            <option value="">Select primary category</option>
            {primaryCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Selecting a primary category will automatically add it to the categories list
          </p>
        </FormField>

        <FormField
          label="Categories *"
          error={errors.categories}
        >
          <div className="relative">
            <button
              type="button"
              onClick={onToggleCategoryDropdown}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${errors.categories ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <span className="text-left truncate">
                {product.categories.length > 0
                  ? `${getSelectedCategoryNames().join(', ')}`
                  : 'Select categories'
                }
              </span>
              <FiChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''
                }`} />
            </button>

            {showCategoryDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 space-y-2">
                  {primaryCategories.map(category => (
                    <label key={category.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isCategorySelected(category.id)}
                        onChange={() => onCategoryToggle(category.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-medium">{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Select one or more primary categories
          </p>

          {childCategories.length > 0 && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Sub-categories
              </label>
              <div className="flex flex-wrap gap-2">
                {childCategories.map(childCat => (
                  <label key={childCat.id} className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isCategorySelected(childCat.id)}
                      onChange={() => onCategoryToggle(childCat.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{childCat.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </FormField>

        <FormField
          label="Main Product Image"
          error={errors.main_image}
          helperText="(Leave unchanged if not updating)"
        >
          <ImageUpload
            image={product.main_image}
            imageUrl={product.main_image_url}
            onImageChange={onMainImageChange}
            onRemove={() => onInputChange({ target: { name: 'main_image', value: null } })}
          />
        </FormField>
      </div>
    </div>
  </div>
);

const ProductVariantsSection = ({
  product, errors, warehouses, onAddVariant, onRemoveVariant, onVariantChange,
  onAddVariantImage, onRemoveVariantImage, onVariantImageChange, validateImageFile
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-xl font-semibold text-gray-900">Product Variants</h2>
      <button
        type="button"
        onClick={onAddVariant}
        className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
      >
        <FiPlus className="w-4 h-4" />
        <span>Add Variant</span>
      </button>
    </div>

    {product.variants.map((variant, variantIndex) => (
      <VariantCard
        key={variantIndex}
        variant={variant}
        variantIndex={variantIndex}
        errors={errors}
        warehouses={warehouses}
        totalVariants={product.variants.length}
        onRemove={onRemoveVariant}
        onChange={onVariantChange}
        onAddImage={onAddVariantImage}
        onRemoveImage={onRemoveVariantImage}
        onImageChange={onVariantImageChange}
        validateImageFile={validateImageFile}
      />
    ))}
  </div>
);

const VariantCard = ({
  variant, variantIndex, errors, warehouses, totalVariants,
  onRemove, onChange, onAddImage, onRemoveImage, onImageChange, validateImageFile
}) => (
  <div className={`border rounded-lg p-6 mb-6 ${variant.should_delete ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
    }`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-gray-900">
        Variant {variantIndex + 1}
        {variant.id && <span className="text-sm text-gray-500 ml-2">(ID: {variant.id})</span>}
        {variant.should_delete && (
          <span className="ml-2 text-red-600 text-sm">(Marked for deletion)</span>
        )}
      </h3>
      {totalVariants > 1 && (
        <button
          type="button"
          onClick={() => onRemove(variantIndex)}
          className="text-red-600 hover:text-red-800 transition-colors"
        >
          <FiTrash2 className="w-5 h-5" />
        </button>
      )}
    </div>

    {!variant.should_delete && (
      <>
        <VariantFields
          variant={variant}
          variantIndex={variantIndex}
          errors={errors}
          warehouses={warehouses}
          onChange={onChange}
        />

        <VariantImages
          variant={variant}
          variantIndex={variantIndex}
          errors={errors}
          onAddImage={onAddImage}
          onRemoveImage={onRemoveImage}
          onImageChange={onImageChange}
          validateImageFile={validateImageFile}
        />
      </>
    )}
  </div>
);

const VariantFields = ({ variant, variantIndex, errors, warehouses, onChange }) => {
  const fields = [
    { key: 'attributes', label: 'Attributes *', type: 'text', placeholder: 'e.g., Red / XL', required: true },
    { key: 'title', label: 'Variant Title *', type: 'text', placeholder: 'e.g., Red Rose', required: true },
    { key: 'warehouse_id', label: 'Warehouse *', type: 'warehouse', options: warehouses, required: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['INR', 'USD', 'EUR'] },
    { key: 'gst', label: 'GST (%)', type: 'number', step: '0.01', placeholder: '18' },
    { key: 'mrp', label: 'MRP', type: 'number', step: '0.01', placeholder: '110.00' },
    { key: 'price', label: 'Selling Price *', type: 'number', step: '0.01', placeholder: '100.50', required: true },
    { key: 'discount', label: 'Discount (%)', type: 'number', step: '0.01', placeholder: '10' },
    { key: 'stock', label: 'Stock Quantity *', type: 'number', placeholder: '10', required: true },
    { key: 'weight', label: 'Weight (kg)', type: 'number', step: '0.01', placeholder: '1.2' },
    { key: 'length', label: 'Length (cm)', type: 'number', step: '0.01', placeholder: '10' },
    { key: 'width', label: 'Width (cm)', type: 'number', step: '0.01', placeholder: '5' },
    { key: 'height', label: 'Height (cm)', type: 'number', step: '0.01', placeholder: '2' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {fields.map(field => (
        <FormField
          key={field.key}
          label={field.label}
          error={errors[`variant_${variantIndex}_${field.key}`]}
        >
          {field.type === 'select' ? (
            <select
              value={variant[field.key]}
              onChange={(e) => onChange(variantIndex, field.key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {field.options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : field.type === 'warehouse' ? (
            <select
              value={variant[field.key]}
              onChange={(e) => onChange(variantIndex, field.key, e.target.value)}
              required={field.required}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_${field.key}`] ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">Select Warehouse</option>
              {field.options.map(warehouse => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} - {warehouse.city}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              step={field.step}
              value={variant[field.key]}
              onChange={(e) => onChange(variantIndex, field.key, e.target.value)}
              required={field.required}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`variant_${variantIndex}_${field.key}`] ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={field.placeholder}
            />
          )}
        </FormField>
      ))}
    </div>
  );
};

const VariantImages = ({
  variant, variantIndex, errors, onAddImage, onRemoveImage, onImageChange, validateImageFile
}) => (
  <div className="border-t pt-6">
    <div className="flex justify-between items-center mb-4">
      <h4 className="text-md font-medium text-gray-900">Variant Images</h4>
      <button
        type="button"
        onClick={() => onAddImage(variantIndex)}
        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
      >
        <FiPlus className="w-4 h-4" />
        <span className="text-sm">Add Image</span>
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {variant.images.map((image, imageIndex) => (
        <VariantImageCard
          key={imageIndex}
          image={image}
          imageIndex={imageIndex}
          variantIndex={variantIndex}
          totalImages={variant.images.length}
          errors={errors}
          onRemove={onRemoveImage}
          onChange={onImageChange}
          validateImageFile={validateImageFile}
        />
      ))}
    </div>
  </div>
);

const VariantImageCard = ({
  image, imageIndex, variantIndex, totalImages, errors, onRemove, onChange, validateImageFile
}) => (
  <div className={`border rounded-lg p-4 ${image.should_delete ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
    }`}>
    <div className="flex justify-between items-center mb-3">
      <span className="text-sm font-medium text-gray-700">
        Image {imageIndex + 1}
        {image.id && <span className="text-xs text-gray-500 ml-1">(ID: {image.id})</span>}
        {image.should_delete && (
          <span className="ml-1 text-red-600 text-xs">(Marked for deletion)</span>
        )}
      </span>
      {totalImages > 1 && (
        <button
          type="button"
          onClick={() => onRemove(variantIndex, imageIndex)}
          className="text-red-500 hover:text-red-700 transition-colors"
        >
          <FiX className="w-4 h-4" />
        </button>
      )}
    </div>

    {!image.should_delete && (
      <div className="space-y-3">
        <ImageUpload
          image={image.image}
          imageUrl={image.image_url}
          onImageChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              const error = validateImageFile(file);
              if (!error) {
                onChange(variantIndex, imageIndex, 'image', file, file.name);
              }
            }
          }}
          onRemove={() => onChange(variantIndex, imageIndex, 'image', null)}
        />

        <FormField
          label="Alt Text *"
          error={errors[`variant_${variantIndex}_alt_${imageIndex}`]}
        >
          <input
            type="text"
            value={image.alt_text}
            onChange={(e) => onChange(variantIndex, imageIndex, 'alt_text', e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors[`variant_${variantIndex}_alt_${imageIndex}`] ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Image description"
          />
          <p className="mt-1 text-xs text-gray-500">
            Alt text is automatically generated from filename but can be edited
          </p>
        </FormField>
      </div>
    )}
  </div>
);

const ImageUpload = ({ image, imageUrl, onImageChange, onRemove }) => (
  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
    {imageUrl || image ? (
      <div className="relative w-full h-full">
        <img
          src={
            imageUrl
              ? `${process.env.REACT_APP_STORE_API_URL}${imageUrl}`
              : URL.createObjectURL(image)
          }
          alt="Preview"
          className="w-full h-full object-cover rounded-lg"
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
        >
          <FiX className="w-3 h-3" />
        </button>
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center">
        <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
        <p className="text-xs text-gray-500">Upload Image</p>
      </div>
    )}
    <input
      type="file"
      accept="image/*"
      onChange={onImageChange}
      className="hidden"
    />
  </label>
);

const FormField = ({ label, error, helperText, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
      {helperText && (
        <span className="text-xs text-gray-500 ml-1">{helperText}</span>
      )}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-sm text-red-600">{error}</p>
    )}
  </div>
);

const FormActions = ({ onCancel, isSubmitting }) => (
  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
    <button
      type="button"
      onClick={onCancel}
      className="px-8 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
      disabled={isSubmitting}
    >
      Cancel
    </button>
    <button
      type="submit"
      disabled={isSubmitting}
      className="flex items-center space-x-2 px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {isSubmitting ? (
        <>
          <FiRotateCw className="w-4 h-4 animate-spin" />
          <span>Updating Product...</span>
        </>
      ) : (
        <>
          <FiSave className="w-4 h-4" />
          <span>Update Product</span>
        </>
      )}
    </button>
  </div>
);

export default VendorProductUpdate;